import {
  BookingType,
  GateOption,
  GateType,
  ParkingSession,
  UserType,
} from './utilTypes';

import { ParkingType } from './utilTypes';

export const buildParkingGateOptions = (
  parking: ParkingType,
  currentUser: UserType | null,
  userBooking: BookingType | null,
) => {
  const gateOptions: GateOption[] = [];

  if (!parking?.devices) {
    return gateOptions;
  }

  parking.devices.forEach((device, idx) => {
    for (let i = 1; i <= device.gates.length; i++) {
      const currentGate = device.gates[i - 1];
      const ongoingSessions = getOngoingSessions(currentGate);
      const ongoingBooking = getOngoingBooking(currentGate);
      const canUnlock = checkIfCanUnlockGate(currentGate, currentUser);
      const hasActiveUserSession = ongoingSessions.some((session) =>
        isOwnerOfSession(session, currentUser),
      );
      gateOptions.push({
        value: `${device.mqttId}/${currentGate.order}`,
        label: currentGate.alias,
        capacity: currentGate.capacity,
        isSingularGate: checkIfGateIsSingular(currentGate),
        canUnlock,
        hasBookingInFuture: checkIfUserHasUpcomingBookingOnGate(
          currentGate,
          userBooking,
          currentUser,
        ),
        hasActiveUserSession,
        hasOtherUserSession:
          !canUnlock &&
          !hasActiveUserSession &&
          (ongoingSessions.some(
            (session) => !isOwnerOfSession(session, currentUser),
          ) ||
            (!!ongoingBooking &&
              !isOwnerOfBooking(ongoingBooking, currentUser))),
        hasOtherUserOngoingBooking:
          !!ongoingBooking && !isOwnerOfBooking(ongoingBooking, currentUser),
      });
    }
  });

  return gateOptions;
};

export const checkIfCanUnlockGate = (gate: GateType, user: UserType | null) => {
  if (gate.status === 'disabled') {
    return false;
  }

  const ongoingSessions = getOngoingSessions(gate);
  const booking = getOngoingBooking(gate) || null;
  const capacity = gate.capacity ?? 1;

  const isSessionOwner = ongoingSessions.some((session) =>
    isOwnerOfSession(session, user),
  );
  const isBookingOwner = isOwnerOfBooking(booking, user);

  if (isSessionOwner) {
    return true;
  }

  if (booking && !isBookingOwner) {
    return false;
  }

  if (ongoingSessions.length === 0 && booking && isBookingOwner) {
    return true;
  }

  return ongoingSessions.length < capacity;
};

const getOngoingBooking = (gate: GateType) => {
  return gate.bookings.find((booking) => {
    const bookingStart = new Date(booking.startsAt);
    const bookingEnd = new Date(booking.endsAt);
    const now = new Date();

    return bookingStart <= now && bookingEnd > now;
  });
};

export const getOngoingSessions = (gate: GateType) => {
  const now = new Date();

  return gate.sessions.filter((session) => {
    const sessionStart = new Date(session.startedAt);
    return sessionStart <= now && !session.endedAt;
  });
};

export const isOwnerOfSession = (
  session: ParkingSession | null,
  user: UserType | null,
) => {
  if (!session) return false;

  return session.user.id === user?.id;
};

export const isOwnerOfBooking = (
  booking: BookingType | null,
  user: UserType | null,
) => {
  if (!booking) return false;

  return booking.user.id === user?.id;
};

export const getParkingGatesCount = (parking: ParkingType) => {
  if (!parking?.devices) {
    return 0;
  }

  if (parking?.devices.length === 0) {
    return 1;
  }

  return parking.devices.reduce((acc, device) => {
    return acc + device.gatesCount;
  }, 0);
};

export const checkIfGateIsSingular = (gate: GateType) => {
  return gate?.capacity === 1;
};

export const checkIfUserHasUpcomingBookingOnGate = (
  gate: GateType,
  userBooking: BookingType | null,
  currentUser: UserType | null,
): boolean => {
  if (!gate || !currentUser) return false;

  const hasBookingOnGate = gate.bookings?.some(
    (booking) => booking.user.id === currentUser.id,
  );
  if (hasBookingOnGate) return true;

  if (userBooking && gate.id === userBooking.gate.id) return true;

  return false;
};
